import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/components/AuthLayout';
import { requestPasswordReset } from '@/lib/auth';

export default function ResetSentPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const email = (state as { email?: string } | null)?.email;
  const [resent, setResent] = useState(false);
  const [pending, setPending] = useState(false);

  /*
   * Reached by bookmark or a typed URL, there is no address to confirm and
   * nothing was sent — showing the confirmation anyway would be a screen that
   * lies. Back to the form instead.
   */
  if (!email) return <Navigate to="/forgot-password" replace />;

  const resend = async () => {
    if (pending) return;
    setPending(true);
    await requestPasswordReset(email);
    setPending(false);
    /*
     * Says "sent again" whatever happened — including when the server declined
     * because the last one went out under a minute ago. Reporting that back
     * would tell an unauthenticated caller that the address has an account.
     */
    setResent(true);
  };

  return (
    <AuthLayout
      title="Check your email"
      subtitle="If that address belongs to a studio account, a reset link is on its way."
      footnote="For security we show this message whether or not the address exists."
    >
      <div
        className="flex items-start gap-3 rounded-xl px-4 py-3.5"
        style={{ background: 'var(--in)' }}
      >
        <span
          className="mt-1.5 h-2 w-2 flex-none rounded-full"
          style={{ background: 'var(--ok)' }}
        />
        <span className="text-[13.5px] leading-[1.6]" style={{ color: 'var(--muted)' }}>
          Sent to <span style={{ color: 'var(--text)' }}>{email}</span>. Look in spam if it does not
          arrive within a few minutes.
        </span>
      </div>
      <button
        type="button"
        onClick={resend}
        disabled={pending || resent}
        className="btn-outline focus-ring min-h-[42px] cursor-pointer p-[11px] text-[13.5px] disabled:cursor-default disabled:opacity-55"
        style={{ color: 'var(--text)' }}
      >
        {resent ? 'Link sent again' : pending ? 'Sending…' : 'Resend link'}
      </button>
      <button
        type="button"
        onClick={() => navigate('/login')}
        className="btn-quiet focus-ring min-h-[32px] cursor-pointer p-1 text-[13.5px]"
      >
        ← Back to sign in
      </button>
    </AuthLayout>
  );
}
